import { useWindowSize } from "@uidotdev/usehooks";
import { useEffect, useState } from "react";
import axios from "axios";

import MetaSection from "@/elements/MetaSection/MetaSection";
import { Mixpanel } from "@/elements/Mixpanel";
import PageNotFound from "../404";
import Actions from "@/elements/Actions";
import IndexDetailPage from "@/components/StocksDetail/IndexDetail/IndexDetailPage";
import { cn } from "@/lib/utils";
import TopBar from "@/components/StocksDetail/TopBar/TopBar";
import FNO from "@/components/Markets/Fno/FNO";
import OptionChain from "@/components/StocksDetail/OptionChain";
import ScreenerFAQSection from "@/components/Screeners/ScreenerFAQSection";

export default function StockDetails({ stockDetails, data, isPaidUserPage, seoData, preActive }) {
    const [active, setActive] = useState(preActive);
    const [activeChartType, setActiveChartType] = useState(1);
    const scriptData = data?.scriptData;

    useEffect(() => {
        setActive(preActive);
        if (data?.nseSymbol) {
            setActiveChartType(1);
        } else {
            setActiveChartType(2);
        }

        Mixpanel.pageView({
            page: "Index_sdp",
            finCode: stockDetails?.finCode,
            symbol: stockDetails?.symbol,
            compName: stockDetails?.compName,
            tab: active
        });
    }, [stockDetails?.finCode, preActive]);
    if (stockDetails?.finCode && stockDetails?.finCode !== "") {
        return (
            <>
                <MetaSection
                    title={`${stockDetails?.symbol} Share/Stock Price Today Live BSE/NSE | Univest `}
                    desc={`${stockDetails?.symbol} Share/Stock price today Live NSE/BSE - Get ${stockDetails?.symbol} stock price with Fundamentals, Performance, Profit & Loss, and Latest Insights.`}
                    keyWords="bombay stock exchange, Indian stock market, bse, national stock exchange, Nifty, nse, sensex, stock exchange, stock market, stock research tool, smart investment, investment ideas, stock market tips"
                />

                <div
                    className={cn("select-none font-Inter", {
                        "pt-20 md:pt-0 h-full overflow-hidden": isPaidUserPage,
                        "pt-24 max-w-screen-xl mx-auto": !isPaidUserPage
                    })}
                >
                    <TopBar
                        section={[
                            { id: "overview", text: "Overview" },
                            { id: "fno", text: "F&O" },
                            { id: "charts", text: "Charts" },
                            { id: "option-chain", text: "Option chain" },
                            { id: "stocks", text: "Stocks" }
                        ]}
                        stockDetails={stockDetails}
                        optionChainAvailable={true}
                        isPaidUserPage={isPaidUserPage}
                        active={active}
                        setActive={(e) => {
                            setActive(e);
                        }}
                        isFNO={stockDetails?.isFNO}
                    />

                    <div
                        className={cn("lg:ml-8 h-full mb-8 w-full  ", {
                            "lg:ml-0 overflow-y-auto mb-0 h-[calc(100%-96px)]": isPaidUserPage
                        })}
                    >
                        {active === "overview" && (
                            <IndexDetailPage
                                setActive={setActive}
                                preData={data}
                                activeChartType={activeChartType}
                                name={stockDetails?.finCode}
                                setActiveChartType={setActiveChartType}
                                isPaidUserPage={isPaidUserPage}
                            />
                        )}
                        {active === "fno" && (
                            <FNO
                                isPaidUserPage={isPaidUserPage}
                                data={data}
                                activeChartType={activeChartType}
                                setActiveChartType={setActiveChartType}
                                stocksDetail={stockDetails}
                                scriptData={scriptData}
                            />
                        )}
                        {active === "charts" && (
                            <iframe
                                className="w-full h-full overflow-hidden min-h-[500px]"
                                src={Actions.toChartUrl(
                                    scriptData?.[activeChartType === 1 ? "NSE" : "BSE"],
                                    activeChartType === 1 ? "NSE" : "BSE",
                                    data?.instrumentName,
                                    data?.nseSymbol ?? data?.bseSymbol
                                )}
                            />
                        )}
                        {active === "option-chain" && (
                            <OptionChain
                                name={stockDetails?.finCode}
                                scriptData={scriptData?.[activeChartType === 1 ? "NSE" : "BSE"]}
                                source={"stock_details"}
                            />
                        )}
                        {active === "stocks" && (
                            <IndexDetailPage
                                setActive={setActive}
                                preData={data}
                                activeChartType={activeChartType}
                                name={stockDetails?.finCode}
                                setActiveChartType={setActiveChartType}
                                isPaidUserPage={isPaidUserPage}
                                showAll={false}
                            />
                        )}
                    </div>
                    {!isPaidUserPage && (
                        <>
                            {seoData.content && (
                                <div
                                    id="screener-seo"
                                    className="font-Inter mx-4 my-8 "
                                    dangerouslySetInnerHTML={{ __html: seoData.content }}
                                />
                            )}
                            <ScreenerFAQSection data={JSON.parse(seoData.faqs ?? "[]")} />
                        </>
                    )}
                </div>
            </>
        );
    } else {
        return <PageNotFound />;
    }
}

export async function getServerSideProps({ query, res, req }) {
    const { params } = query;
    const symbol = params?.[0]?.replaceAll("-", " ");

    const [resp, res2, footerContentRes] = await Promise.all([
        axios
            .get(
                `${process.env.apiBaseURL}/resources/stock-details/symbol-fincode-exchange?symbol=${encodeURIComponent(
                    symbol?.toUpperCase()
                )}&is-index=true`
            )
            .catch((e) => ({})),
        axios
            .get(`${process.env.apiBaseURL}/resources/web/seo-contents/indices-${symbol?.replaceAll(" ", "-")}`)
            .catch(() => ({})),
        axios.get(`${process.env.apiBaseURL}/resources/web/footer-sections`).catch(() => ({ data: null }))
    ]);

    let finCode = (params?.[1] ? resp.data?.finCode : "") ?? "";
    let data = resp?.data?.pricesData ?? {};
    return {
        props: {
            stockDetails: {
                finCode,
                isFNO: data?.expiryDateWithFinCodeList?.length > 0,
                expiryDateWithFinCodeList: data?.expiryDateWithFinCodeList ?? [],
                symbol: (params?.[0] ?? "")?.toUpperCase(),
                compName: params?.[1] ?? params?.[0] ?? ""
            },
            footerContentData: footerContentRes?.data ?? null,
            pageName: "stock-details",
            data,
            params: params?.[0],
            pageName: "stock-details",
            isPaidUserPage: !!req.cookies["auth_token"],
            preActive: params?.[2] ?? "overview",
            seoData: res2.data ?? {}
        }
    };
}
